import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, useNavigate, Navigate, Outlet } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import ServicesSection from "./components/ServicesSection";
import WhyUsSection from "./components/WhyUsSection";
import PortfolioSection from "./components/PortfolioSection";
import TestimonialsSection from "./components/TestimonialsSection";
import FAQSection from "./components/FAQSection";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import CustomCursor from "./components/CustomCursor";
import AnimatedBackground from "./components/AnimatedBackground";
import MouseGlow from "./components/MouseGlow";
import WhatsAppFloating from "./components/WhatsAppFloating";
import ChatBot from "./components/ChatBot";
import ChiSiamoPage from "./pages/ChiSiamoPage";
import CaseStudyPage from "./pages/CaseStudyPage";
import PortfolioPage from "./pages/PortfolioPage";
import AIVideoPage from "./pages/AIVideoPage";
import AIVideoSection from "./components/AIVideoSection";
import { SEO } from "./components/SEO";
import { SUPPORTED_LANGS, detectBrowserLang, localizedPath, parsePathname } from "./utils/routes";

// Scroll to top on route change - with iOS Safari fix
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
  }, [pathname]);
  
  return null;
};

// Root redirect: / → /:detectedLang
const RootRedirect = () => {
  const lang = detectBrowserLang();
  return <Navigate to={`/${lang}`} replace />;
};

// Language layout: syncs URL lang with i18n
const LanguageLayout = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!SUPPORTED_LANGS.includes(lang)) {
      navigate(`/${detectBrowserLang()}`, { replace: true });
      return;
    }
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n, navigate]);
  
  if (!SUPPORTED_LANGS.includes(lang)) return null;
  
  return <Outlet />;
};

// Home Page Component
const HomePage = () => {
  return (
    <div className="bg-[#0a0a0a] relative film-grain overflow-x-hidden">
      <SEO page="home" />
      <AnimatedBackground />
      <MouseGlow />
      <CustomCursor />
      <Navbar />
      <main className="relative" style={{ zIndex: 1 }}>
        <HeroSection />
        <ServicesSection />
        <WhyUsSection />
        <PortfolioSection />
        <AIVideoSection />
        <TestimonialsSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

// Legacy redirect for old /progetto/:id URLs
const LegacyProjectRedirect = () => {
  const lang = detectBrowserLang();
  return <Navigate to={`/${lang}`} replace />;
};

import { PageTransitionProvider } from "./components/PageTransition";

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <PageTransitionProvider>
            <ScrollToTop />
            <WhatsAppFloating />
          <ChatBot />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/:lang" element={<LanguageLayout />}>
              <Route index element={<HomePage />} />
              {/* About page - all translated slugs */}
              <Route path="chi-siamo" element={<ChiSiamoPage />} />
              <Route path="about-us" element={<ChiSiamoPage />} />
              <Route path="uber-uns" element={<ChiSiamoPage />} />
              <Route path="a-propos" element={<ChiSiamoPage />} />
              {/* Portfolio page - same slug for all languages */}
              <Route path="portfolio" element={<PortfolioPage />} />
              {/* AI Video page - translated slugs */}
              <Route path="video-ai" element={<AIVideoPage />} />
              <Route path="ai-video" element={<AIVideoPage />} />
              {/* Legacy slugs kept for old links */}
              <Route path="ki-video" element={<AIVideoPage />} />
              <Route path="video-ia" element={<AIVideoPage />} />
              {/* Case study - all translated slugs */}
              <Route path="progetto/:slug" element={<CaseStudyPage />} />
              <Route path="project/:slug" element={<CaseStudyPage />} />
              <Route path="projekt/:slug" element={<CaseStudyPage />} />
              <Route path="projet/:slug" element={<CaseStudyPage />} />
            </Route>
            {/* Legacy redirects for old URLs */}
            <Route path="/chi-siamo" element={<Navigate to="/it/chi-siamo" replace />} />
            <Route path="/progetto/:projectId" element={<LegacyProjectRedirect />} />
          </Routes>
        </PageTransitionProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
